import { useEffect, useState, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ConfigurationTable, ColumnDefinition } from './ConfigurationTable';
import { useAtom } from 'jotai';
import { assemblyFields, riskConfigData } from '../state';
import { stripTypenameFromRemediationConfig } from '../utils';
import {
  RemediationScoreCofField,
  RemediationScoreCofSection,
  TemplateTypeEnum,
  useAllTemplatesForBuilderLazyQuery,
  AssemblyTemplateField,
  useUpdateRemediationScoreConfigMutation,
} from '@/__generated__/graphql';
import { Box, MenuItem, TextField } from '@mui/material';
import { PageLoader } from '@/components/shared/PageLoader';

// Type definitions
export type CofConfigItem = {
  id: string;
  fieldKey: string;
  score: number;
};

export const CofConfiguration = ({
  structureId,
  templateId,
}: {
  structureId: string;
  templateId: string;
}) => {
  const [cofItems, setCofItems] = useState<CofConfigItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [allAssemblyTemplates, setAllAssemblyTemplates] = useAtom(assemblyFields);
  const [selectedComponent, setSelectedComponent] = useState<AssemblyTemplateField | null>(null);
  const [riskConfig, setRiskConfig] = useAtom(riskConfigData);

  const [allTemplates, { data, loading }] = useAllTemplatesForBuilderLazyQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  const [updateRemediationScoreConfig] = useUpdateRemediationScoreConfigMutation();

  useEffect(() => {
    if (!allAssemblyTemplates) {
      allTemplates();
    }
  }, [allAssemblyTemplates, allTemplates]);

  useEffect(() => {
    if (data?.allTemplates && !allAssemblyTemplates) {
      setAllAssemblyTemplates(data?.allTemplates.at(0)?.fields);
    }
  }, [allAssemblyTemplates, data?.allTemplates, setAllAssemblyTemplates]);

  useEffect(() => {
    setCofItems([]);
    setEditingId(null);
    setSelectedComponent(null);
  }, []);

  useEffect(() => {
    if (riskConfig && riskConfig.cof && riskConfig.cof.fields) {
      const cofField = riskConfig.cof.fields[0];
      if (cofField && cofField.optionScores) {
        // Find the selected component based on fieldKey
        const matchingComponent = allAssemblyTemplates?.find(
          (field) => field.key === cofField.fieldKey
        );
        if (matchingComponent) {
          setSelectedComponent(matchingComponent);
        }

        // Convert optionScores object to cofItems array format
        const convertedItems: CofConfigItem[] = Object.entries(cofField.optionScores).map(
          ([key, value]) => ({
            id: key,
            fieldKey: key,
            score: value as number,
          })
        );
        setCofItems(convertedItems);
      }
    }
  }, [riskConfig, allAssemblyTemplates]);

  const columns = useMemo((): ColumnDefinition<CofConfigItem>[] => {
    return [
      {
        key: 'fieldKey',
        label: 'Name',
        width: 2.4,
        type: 'select',
        options:
          selectedComponent?.options?.map((option: string) => ({
            value: option,
            label: option,
          })) || [],
      },
      {
        key: 'score',
        label: 'Score',
        width: 2.4,
        type: 'number',
        inputProps: { step: 0.01, min: 0 },
      },
    ];
  }, [selectedComponent]);

  const handleEditStart = (item: CofConfigItem) => {
    setEditingId(item.id);
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };

  const handleEditSave = (
    id: string,
    field: keyof Omit<CofConfigItem, 'id'>,
    value: string | number
  ) => {
    const updatedItems = cofItems.map((item) =>
      item.id === id
        ? { ...item, [field]: field === 'score' ? parseFloat(value as string) : value }
        : item
    );
    setCofItems(updatedItems);
    setEditingId(null);
    saveCofConfiguration(updatedItems);
  };

  const handleInlineChange = (
    id: string,
    field: keyof Omit<CofConfigItem, 'id'>,
    value: string | number
  ) => {
    setCofItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, [field]: field === 'score' ? parseFloat(value as string) : value }
          : item
      )
    );
  };

  const handleAddNew = (newItem: Omit<CofConfigItem, 'id'>) => {
    const itemWithId: CofConfigItem = {
      ...newItem,
      id: Date.now().toString(),
      score: typeof newItem.score === 'string' ? parseFloat(newItem.score) : newItem.score,
    };
    const updatedItems = [...cofItems, itemWithId];
    setCofItems(updatedItems);
    saveCofConfiguration(updatedItems);
  };

  const handleDelete = (item: CofConfigItem) => {
    const updatedItems = cofItems.filter((cofItem) => cofItem.id !== item.id);
    setCofItems(updatedItems);
    saveCofConfiguration(updatedItems);
  };

  // Shared function to save CoF configuration data
  const saveCofConfiguration = async (itemsToSave?: CofConfigItem[]) => {
    const items = itemsToSave || cofItems;
    if (!selectedComponent || items.length === 0) return;

    const optionScores: Record<string, number> = {};
    items.forEach((item) => {
      optionScores[item.fieldKey] = item.score;
    });

    const cofField: RemediationScoreCofField = {
      fieldKey: selectedComponent.key || selectedComponent.id,
      optionScores: optionScores,
    };

    const cofData: RemediationScoreCofSection = {
      fields: [cofField],
    };

    try {
      await updateRemediationScoreConfig({
        variables: {
          input: {
            structureId,
            templateId,
            remediationScoreConfig: {
              area: riskConfig.area,
              poi: riskConfig.poi,
              cof: cofData,
            },
          },
        },
      }).then((response) => {
        if (response.data?.updateRemediationScoreConfig?.remediationScoreConfig) {
          const cleanConfig = stripTypenameFromRemediationConfig(
            response.data.updateRemediationScoreConfig.remediationScoreConfig
          );
          setRiskConfig(cleanConfig);
        }
      });
    } catch (error) {
      console.error('Failed to save CoF configuration:', error);
    }
  };

  const handleComponentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFieldComponent = allAssemblyTemplates?.find(
      (field) => field.id === event.target.value
    );
    setSelectedComponent(selectedFieldComponent || null);
    setCofItems([]);
  };

  return (
    <>
      <Typography variant="h4">CoF Configuration</Typography>

      {loading && <PageLoader />}
      {!loading && (
        <Stack direction="row" gap={2} alignItems="center" pb={2}>
          <Typography variant="body2">
            Select the component column you want to set as CoF:
          </Typography>
          <TextField
            select
            size="small"
            sx={{ width: '20%' }}
            value={selectedComponent?.id || ''}
            onChange={handleComponentChange}
          >
            {allAssemblyTemplates?.map((field) => {
              if (field.options && field.options.length > 0) {
                return (
                  <MenuItem key={field.id} value={field.id}>
                    {field.name}
                  </MenuItem>
                );
              }
            })}
          </TextField>
        </Stack>
      )}

      {selectedComponent && (
        <Box width="100%">
          {selectedComponent && (
            <ConfigurationTable
              items={cofItems}
              columns={columns}
              editingId={editingId}
              emptyMessage="No CoF configurations added yet. Click '+ Add New Row' to create one."
              onEditStart={handleEditStart}
              onEditCancel={handleEditCancel}
              onEditSave={handleEditSave}
              onInlineChange={handleInlineChange}
              onAddNew={handleAddNew}
              onDelete={handleDelete}
            />
          )}
        </Box>
      )}
      <Stack direction="row" alignItems="center" py={2}>
        <Typography variant="h6">Asset Equation: </Typography>
        <Typography variant="body2" ml={1}>
          (Area Score + POI Score) * CoF Score
        </Typography>
      </Stack>
    </>
  );
};
